import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBell, FaBox, FaMoneyBillWave, FaComment, FaStar, FaShoppingCart, FaCog } from 'react-icons/fa';
import api from '../utils/api';
import { getSocket } from '../utils/socket';
import { getImageUrl } from '../utils/imageUtils';
import { toLocalizedNumber } from '../utils/localeUtils';
import './NotificationBell.css';

const notificationSound = new Audio('/notificationsound.wav');
notificationSound.volume = 0.5;

function playNotificationSound() {
  try {
    notificationSound.currentTime = 0;
    notificationSound.play().catch(() => {});
  } catch (_) {}
}

function normalizeSocketNotification(payload) {
  return {
    _id: payload._id || payload.id,
    type: payload.type,
    title: payload.title,
    message: payload.message,
    link: payload.link,
    image: payload.image,
    entityName: payload.entityName,
    isRead: payload.isRead ?? false,
    createdAt: payload.createdAt,
  };
}

const NotificationBell = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      setLoading(true);
      const res = await api.get('/notifications?limit=30');
      const data = res?.data ?? res;
      const list = Array.isArray(data?.notifications) ? data.notifications : Array.isArray(data) ? data : [];
      const count = typeof data?.unreadCount === 'number' ? data.unreadCount : 0;
      setNotifications(list);
      setUnreadCount(count);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Failed to fetch notifications', err);
      }
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Ensure socket connects when user is logged in (e.g. after login or when opening a page with header)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) getSocket();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const timer = setTimeout(fetchNotifications, 300);
    return () => clearTimeout(timer);
  }, []);

  // Attach socket listener for real-time notifications; re-attach on connect so we never miss
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onNotification = (payload) => {
      const id = payload._id || payload.id;
      setNotifications((prev) => {
        if (id && prev.some((n) => (n._id || n.id) === id)) return prev;
        playNotificationSound();
        return [normalizeSocketNotification(payload), ...prev];
      });
      setUnreadCount((prev) => prev + 1);
    };

    const attachListener = () => {
      socket.off('notification', onNotification);
      socket.on('notification', onNotification);
    };

    attachListener();
    socket.on('connect', attachListener);

    return () => {
      socket.off('notification', onNotification);
      socket.off('connect', attachListener);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark read failed', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all read failed', err);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { minutes: toLocalizedNumber(diffMins) });
    if (diffHours < 24) return t('notifications.hoursAgo', { hours: toLocalizedNumber(diffHours) });
    if (diffDays < 7) return t('notifications.daysAgo', { days: toLocalizedNumber(diffDays) });
    return d.toLocaleDateString();
  };

  const typeIcon = (type) => {
    const icons = {
      order: FaBox,
      payment: FaMoneyBillWave,
      message: FaComment,
      review: FaStar,
      product: FaShoppingCart,
      system: FaCog,
    };
    const Icon = icons[type] || FaBell;
    return <Icon className="notification-type-icon" />;
  };

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => {
          const willOpen = !isOpen;
          setIsOpen(willOpen);
          if (willOpen) fetchNotifications();
        }}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <FaBell className="notification-bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-bell-badge">
            {unreadCount > 99 ? toLocalizedNumber('99+') : toLocalizedNumber(unreadCount)}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-bell-dropdown">
          <div className="notification-bell-dropdown-header">
            <span className="notification-bell-dropdown-title">{t('notifications.bellTitle')}</span>
            <div className="notification-bell-dropdown-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="notification-bell-mark-all"
                  onClick={handleMarkAllRead}
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
              <Link to="/notifications" className="notification-bell-view-all" onClick={() => setIsOpen(false)}>
                {t('notifications.viewAll')}
              </Link>
            </div>
          </div>
          <div className="notification-bell-list">
            {loading ? (
              <div className="notification-bell-loading">{t('notifications.loading')}</div>
            ) : notifications.length === 0 ? (
              <div className="notification-bell-empty">
                <p>{t('notifications.noNotificationsYet')}</p>
                <button type="button" className="notification-bell-retry" onClick={(e) => { e.preventDefault(); fetchNotifications(); }}>
                  {t('notifications.refresh')}
                </button>
              </div>
            ) : (
              notifications.map((n, idx) => (
                <Link
                  key={n._id || idx}
                  to={n.link || '#'}
                  className={`notification-bell-item ${n.isRead ? 'read' : ''}`}
                  onClick={() => {
                    if (!n.isRead) handleMarkAsRead(n._id);
                    setIsOpen(false);
                  }}
                >
                  {(n.image || n.entityName) ? (
                    <span className="notification-bell-item-thumb">
                      {n.image ? (
                        <img src={getImageUrl(n.image)} alt="" className="notification-bell-item-img" />
                      ) : (
                        <span className="notification-bell-item-icon">{typeIcon(n.type)}</span>
                      )}
                    </span>
                  ) : (
                    <span className="notification-bell-item-icon">{typeIcon(n.type)}</span>
                  )}
                  <div className="notification-bell-item-body">
                    {n.entityName && <div className="notification-bell-item-entity">{n.entityName}</div>}
                    <div className="notification-bell-item-title">{n.title}</div>
                    <div className="notification-bell-item-message">{n.message}</div>
                    <div className="notification-bell-item-time">{formatTime(n.createdAt)}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
